/**
 * Deskryptory kolumn widoku SQL — most między kolumnami wyniku zapytania widoku
 * (`AdditionalViewColumn`, odpowiedź endpointu kolumn) a kontraktem układu kolumn tabel
 * osadzonych (`@saas/shared` `embedded-table-columns`). Używa go edytor układu kolumn w kreatorze
 * formatki (klocek „Widok SQL" i cała zakładka); karta rekordu liczy id z tej samej funkcji
 * (`additionalViewColumnIds`).
 */

import {
  type AdditionalViewColumn,
  additionalViewColumnIds,
  type EmbeddedTableColumnDescriptor,
  type EmbeddedTableColumnKind,
} from '@saas/shared';

/**
 * Rodzaj danych kolumny z klasyfikacji wyniku zapytania (całkowite pokrycie unii `displayType` —
 * nowa klasyfikacja po stronie API nie skompiluje się, dopóki nie dostanie rodzaju):
 * `number` (liczby całkowite) → całkowita, `amount` (numeric/float) → dziesiętna,
 * `date`/`datetime` → data, `bool` → logiczna, `text` → tekst.
 */
const KIND_BY_DISPLAY_TYPE: Record<AdditionalViewColumn['displayType'], EmbeddedTableColumnKind> = {
  text: 'text',
  number: 'integer',
  amount: 'decimal',
  date: 'date',
  datetime: 'date',
  bool: 'boolean',
};

/**
 * Deskryptory kolumn widoku w kolejności zapytania. `id` = `additionalViewColumnIds` (nazwa
 * kolumny, a przy powtórzonej nazwie `nazwa#2`…) — pod nim układ trzyma widoczność, szerokość i
 * etykietę; etykieta domyślna = nazwa kolumny z zapytania. Widok jest tylko do odczytu, więc
 * żadna kolumna nie jest wymagana ani zablokowana (każdą wolno ukryć).
 */
export function additionalViewDescriptors(
  columns: readonly AdditionalViewColumn[],
): EmbeddedTableColumnDescriptor[] {
  const ids = additionalViewColumnIds(columns);
  return columns.map((column, index) => {
    const id = ids[index];
    // `additionalViewColumnIds` zwraca tablicę równoległą do wejścia — brak id to błąd kontraktu.
    if (id === undefined) {
      throw new Error(`additionalViewDescriptors: brak id kolumny ${index} („${column.name}")`);
    }
    return { id, label: column.name, kind: KIND_BY_DISPLAY_TYPE[column.displayType] };
  });
}
